import { useTranslation } from 'react-i18next'
import { Loading } from 'react-daisyui'

/**
 * Progress display for a running ZIP download.
 *
 * The backend delivers only a few, large steps (0.225 / 0.45 / … / 0.9, then 0.95
 * when the ZIP is packed and 1.0 when it is done), so the bar is animated —
 * without the transition it would look stuck and then jump.
 *
 * At `progress === 0` there is nothing meaningful to show yet: fetching the first
 * quarter of the files can take a while, and a 0 % bar reads as "broken". A
 * spinner plus "Download wird vorbereitet …" is shown instead.
 *
 * The bar is never a completion signal — the caller switches to "done" only on
 * `status === 'done'` with a `download_url`.
 */
export default function DownloadProgressBar({ progress, label, className }: {
  progress: number
  /** Overrides the label shown once the bar is visible. */
  label?: string
  className?: string
}) {
  const { t } = useTranslation()
  const percent = Math.round(Math.min(1, Math.max(0, progress)) * 100)

  if (percent <= 0) {
    return <div className={`flex flex-col gap-2 items-center w-full ${className ?? ''}`}>
      <Loading color='primary' />
      <p className='text-center'>{t('common.downloadBeingPrepared', 'Download wird vorbereitet …')}</p>
    </div>
  }

  return <div className={`flex flex-col gap-2 items-center w-full ${className ?? ''}`}>
    <div className='w-full h-2 rounded-full bg-base-300 overflow-hidden'>
      <div
        className='h-full bg-primary rounded-full transition-[width] duration-700 ease-out'
        style={{ width: `${percent}%` }}
      />
    </div>
    <p className='text-center text-sm opacity-70'>
      {label ?? t('common.downloadBeingCreated')} · {percent} %
    </p>
  </div>
}
